import type {
  MediaAsset,
  PlaybackEvent,
  Playlist,
  Schedule,
  Screen,
  ScreenGroup,
} from '@ghostscreen/contracts';
import { i18n } from '../../../i18n';
import { formatRelativeTime } from '../relativeTime';
import { getCardConnectionStatus } from '../screenStatus';
import type { OverviewTone } from './buildOverviewStatus';

export type OverviewActivityItem = {
  id: string;
  text: string;
  timeLabel: string;
  tone: OverviewTone;
  ts: number;
};

type ActivitySeed = {
  id: string;
  text: string;
  tone: OverviewTone;
  ts: number;
};

function parseTs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? null : value;
}

function displayAssetName(filename: string): string {
  const trimmed = filename.trim();
  const withoutExt = trimmed.replace(/\.[a-z0-9]{2,5}$/i, '');
  return withoutExt || trimmed;
}

const RECENT_CREATE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Builds a "what's happened lately" feed from real product signals.
 * No sample/demo rows — empty when nothing recent is available.
 */
export function buildOverviewActivity(input: {
  screens: Screen[];
  playlists: Playlist[];
  playbackEvents: PlaybackEvent[];
  mediaAssets?: MediaAsset[];
  schedules?: Schedule[];
  groups?: ScreenGroup[];
  now?: number;
  limit?: number;
}): OverviewActivityItem[] {
  const now = input.now ?? Date.now();
  const limit = input.limit ?? 4;
  const recentAfter = now - RECENT_CREATE_MS;
  const screenById = new Map(input.screens.map((screen) => [screen.id, screen]));
  const playlistById = new Map(input.playlists.map((playlist) => [playlist.id, playlist]));
  const groupById = new Map((input.groups ?? []).map((group) => [group.id, group]));
  const seeds: ActivitySeed[] = [];

  for (const screen of input.screens) {
    const connection = getCardConnectionStatus(screen);
    const heartbeat = parseTs(screen.lastHeartbeatAt);
    if (connection === 'offline' && heartbeat != null) {
      seeds.push({
        id: `offline-${screen.id}`,
        text: i18n.t('overview:activity.wentOffline', { name: screen.name }),
        tone: 'bad',
        ts: heartbeat,
      });
    }

    const created = parseTs(screen.createdAt);
    if (created != null && created >= recentAfter) {
      seeds.push({
        id: `screen-created-${screen.id}`,
        text: i18n.t('overview:activity.screenAdded', { name: screen.name }),
        tone: 'accent',
        ts: created,
      });
    }
  }

  for (const playlist of input.playlists) {
    const created = parseTs(playlist.createdAt);
    if (created == null || created < recentAfter) continue;
    seeds.push({
      id: `playlist-${playlist.id}`,
      text: i18n.t('overview:activity.playlistReady', { name: playlist.name }),
      tone: 'ok',
      ts: created,
    });
  }

  for (const asset of input.mediaAssets ?? []) {
    const created = parseTs(asset.createdAt);
    if (created == null || created < recentAfter) continue;
    const name = displayAssetName(asset.originalFilename);
    seeds.push({
      id: `media-${asset.id}`,
      text: name
        ? i18n.t('overview:activity.uploaded', { name })
        : i18n.t('overview:activity.fileUploaded'),
      tone: 'accent',
      ts: created,
    });
  }

  for (const schedule of input.schedules ?? []) {
    const updated = parseTs(schedule.updatedAt) ?? parseTs(schedule.createdAt);
    if (updated == null || updated < recentAfter) continue;
    const screenName = schedule.screenId ? screenById.get(schedule.screenId)?.name : undefined;
    const groupName = schedule.screenGroupId
      ? groupById.get(schedule.screenGroupId)?.name
      : undefined;
    const target = schedule.name || screenName || groupName;
    seeds.push({
      id: `schedule-${schedule.id}-${schedule.updatedAt}`,
      text: target
        ? i18n.t('overview:activity.scheduleUpdated', { target })
        : i18n.t('overview:activity.scheduleUpdatedAll'),
      tone: 'accent',
      ts: updated,
    });
  }

  for (const event of input.playbackEvents) {
    // Only a play start is "playback started"; end / interrupted / failed rows of the same play are not.
    if (event.eventType !== 'play_start') continue;
    const playedAt = parseTs(event.playedAt);
    if (playedAt == null) continue;
    const screen = event.screenId ? screenById.get(event.screenId) : undefined;
    const playlist = event.playlistId ? playlistById.get(event.playlistId) : undefined;
    const text = playlist
      ? screen
        ? i18n.t('overview:activity.playlistOnScreen', {
            playlist: playlist.name,
            screen: screen.name,
          })
        : i18n.t('overview:activity.playlistOnUnknownScreen', { playlist: playlist.name })
      : screen
        ? i18n.t('overview:activity.playbackStarted', { screen: screen.name })
        : i18n.t('overview:activity.playbackStartedUnknownScreen');
    seeds.push({
      id: `play-${event.id}`,
      text,
      tone: 'ok',
      ts: playedAt,
    });
  }

  const seen = new Set<string>();
  return seeds
    .filter((seed) => seed.ts <= now)
    .sort((a, b) => b.ts - a.ts)
    .filter((seed) => {
      if (seen.has(seed.id)) return false;
      seen.add(seed.id);
      return true;
    })
    .slice(0, limit)
    .map((seed) => ({
      id: seed.id,
      text: seed.text,
      timeLabel: formatRelativeTime(new Date(seed.ts).toISOString(), now),
      tone: seed.tone,
      ts: seed.ts,
    }));
}
